import { RunReading, RunSnapshot, RunStep, firstValue, normalizeIdentifier, printValue, tidyNumbers, toNumber } from './runs';
import { StepBlock, printedReadings, readingLabel } from './placement';

/**
 * The checks a run records, gathered from wherever its steps made them, so a
 * report can say in one place what was checked, under which conditions, and
 * whether each passed — the results table a bench report leads with.
 *
 * A check is either a step's validation (a rule the template author wrote,
 * with its message) or a yes/no the step recorded: a result a procedure's
 * code computed (`current_below_threshold`) or an operator's answer. A yes/no
 * check carries the numbers its step recorded beside it — what it compared.
 *
 * Procedures repeat a check over the conditions they loop through (a light
 * function, a temperature, a region), naming each in quotes in the step's
 * sections and title. A check's condition is those names; its group is the
 * check itself, the same at every condition.
 */

export interface CheckValue {
  identifier: string;
  label: string;
  value: string;
}

export interface CheckRow {
  key: string;
  /** the group it belongs to: one check, made at each condition */
  group: string;
  /** a validation's group name; a yes/no's is worked out from every label in its group */
  groupName?: string;
  /** this instance's own words: the yes/no's label, or the validation's title */
  label: string;
  step: RunStep;
  /** the conditions it was made at: the names quoted in its step's sections and title, outermost first */
  condition: string[];
  /** for a yes/no, the numbers its step recorded beside it */
  values: CheckValue[];
  passed: boolean;
  /** a validation's message, its numbers tidied; empty for a yes/no */
  message: string;
  source: 'validation' | 'flag';
  /** the readings it accounts for, so a step it shows in full can be left out where it shows */
  parameterIds: string[];
}

export interface CheckGroup {
  key: string;
  /** the check's name, its conditions taken out */
  name: string;
  source: 'validation' | 'flag';
  rows: CheckRow[];
  passed: number;
  failed: number;
  /** the values the group's rows carry, one column each, named without their conditions */
  columns: Array<{ identifier: string; label: string }>;
}

const FLAG_TRUE = new Set(['true', 'yes', 'pass', 'passed']);
const FLAG_FALSE = new Set(['false', 'no', 'fail', 'failed']);
const MAX_VALUES = 4;

/** A reading's single yes/no, or null when it holds anything else. */
export function flagOf(reading: RunReading): boolean | null {
  const values = reading.values.filter((v) => v !== null && v !== '');
  if (values.length !== 1 || reading.records.length > 0) return null;
  const v = String(values[0]).trim().toLowerCase();
  return FLAG_TRUE.has(v) ? true : FLAG_FALSE.has(v) ? false : null;
}

/** The names quoted in a step's sections and title, outermost first, each once: `"Turn Indicator"`, `"Nominal Temperature"`, `"Region 1"`. */
export function stepConditions(step: Pick<RunStep, 'path' | 'title'>): string[] {
  const out: string[] = [];
  for (const text of [...step.path, step.title]) {
    for (const m of Array.from(text.matchAll(/["“”]([^"“”]{1,60})["“”]/g))) {
      const name = m[1].trim();
      if (name && !out.some((o) => o.toLowerCase() === name.toLowerCase())) out.push(name);
    }
  }
  return out;
}

const LINKING = new Set(['at', 'in', 'for', 'of', 'during', 'with', 'on', 'the', 'and', 'to', 'from', 'a', 'an', 'by', 'when', '-', '–', '—', ':', '&']);
const escape = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/**
 * A label with the conditions it names taken out, and the words left hanging
 * by that: "Chamber Ready for Nominal Temperature" → "Chamber Ready".
 */
export function withoutConditions(label: string, conditions: string[]): string {
  let text = ` ${label} `;
  for (const c of conditions.slice().sort((a, b) => b.length - a.length)) {
    text = text.replace(new RegExp(`["“”]?(?<![A-Za-z0-9])${escape(c)}(?![A-Za-z0-9])["“”]?`, 'gi'), ' ');
  }
  const firstWords = new Set(conditions.map((c) => c.split(/\s+/)[0].toLowerCase()));
  // a question keeps its mark, wherever the condition it ended on was
  const end = /[?!]$/.exec(label.trim())?.[0] ?? '';
  const words = text.split(/\s+/).filter((w) => w && !/^[?!.,;:]+$/.test(w));
  // a linking word between two others that survive is kept ("Below Threshold at Turn-On" stays); doubled or dangling ones go
  const kept: string[] = [];
  for (const w of words) {
    const lw = w.toLowerCase();
    if (LINKING.has(lw) && (kept.length === 0 || LINKING.has(kept[kept.length - 1].toLowerCase()))) continue;
    kept.push(w);
  }
  while (kept.length && (LINKING.has(kept[kept.length - 1].toLowerCase()) || firstWords.has(kept[kept.length - 1].toLowerCase()))) kept.pop();
  const out = kept.join(' ').replace(/\s+([?.,;:])/g, '$1').trim();
  if (!out) return label;
  return end && !out.endsWith(end) ? `${out}${end}` : out;
}

/** The words every label starts with, or '' when they share fewer than two. */
function commonStart(labels: string[]): string {
  if (labels.length < 2) return labels[0] ?? '';
  const split = labels.map((l) => l.split(/\s+/));
  const out: string[] = [];
  for (let i = 0; i < split[0].length; i++) {
    const w = split[0][i];
    if (split.every((s) => (s[i] ?? '').toLowerCase() === w.toLowerCase())) out.push(w);
    else break;
  }
  return out.length >= 2 ? out.join(' ') : '';
}

/**
 * One name for labels that differ only in the conditions they name, and in
 * what follows them: each without its conditions, then the words they all
 * start with — "Low Beam Max … upper limit 110%?" and "DRL Max … upper
 * limit 125%?" are "Max … upper limit".
 */
export function sharedName(labels: string[], conditions: string[]): string {
  const stripped = labels.map((l) => withoutConditions(l, conditions));
  const start = commonStart(stripped);
  return withoutConditions(start || stripped[0] || '', conditions);
}

const firstSentence = (message: string) => (message.split(/\n|(?<=\.)\s/)[0] ?? '').trim();

function validationGroup(title: string, message: string): { key: string; name: string } {
  // an untitled validation is named by what its message says, without the numbers filled in
  const name = title.trim() || firstSentence(message).replace(/\(?-?\d[\d.,]*\s*[a-z%°]*\)?/gi, '').replace(/\s+/g, ' ').trim() || 'Validation';
  return { key: `v:${name.toLowerCase()}`, name };
}

/** Every check the run recorded, in step order. */
export function runChecks(run: Pick<RunSnapshot, 'steps'>): CheckRow[] {
  const rows: CheckRow[] = [];
  for (const step of run.steps) {
    const condition = stepConditions(step);
    (step.validations ?? []).forEach((v, i) => {
      const { key, name } = validationGroup(v.title, v.message);
      rows.push({ key: `${step.id}-v${i}`, group: key, groupName: name, label: v.title.trim() || firstSentence(tidyNumbers(v.message)), step, condition, values: [], passed: v.passed, message: tidyNumbers(v.message), source: 'validation', parameterIds: [] });
    });
    const readings = printedReadings(step.readings).filter((r) => r.operationType !== 'File');
    const flags = readings.filter((r) => flagOf(r) !== null);
    if (flags.length === 0) continue;
    // what the check compared: the step's numbers, its inputs first
    const numbers = readings.filter((r) => flagOf(r) === null && toNumber(firstValue(r)) !== null);
    const compared = [...numbers.filter((r) => r.kind === 'input'), ...numbers.filter((r) => r.kind !== 'input')].slice(0, MAX_VALUES);
    const values = compared.map((r) => ({ identifier: normalizeIdentifier(r.identifier) || r.parameterId, label: readingLabel(r.title, r.identifier), value: printValue(firstValue(r)) }));
    for (const flag of flags) {
      const label = readingLabel(flag.title, flag.identifier);
      const id = normalizeIdentifier(flag.identifier);
      rows.push({
        key: `${step.id}-${flag.parameterId}`,
        group: id ? `f:${id}` : `f:${withoutConditions(label, condition).toLowerCase()}`,
        label,
        step,
        condition,
        values,
        passed: flagOf(flag) === true,
        message: '',
        source: 'flag',
        parameterIds: [flag.parameterId, ...compared.map((r) => r.parameterId)],
      });
    }
  }
  return rows;
}

/** Checks gathered by what they check, in the order each first appears. */
export function groupChecks(rows: CheckRow[]): CheckGroup[] {
  const groups = new Map<string, CheckRow[]>();
  for (const row of rows) groups.set(row.group, [...(groups.get(row.group) ?? []), row]);
  return Array.from(groups.entries()).map(([key, members]) => {
    const conditions = Array.from(new Set(members.flatMap((m) => m.condition)));
    const source = members[0].source;
    const name = source === 'validation' ? members[0].groupName ?? members[0].label : sharedName(members.map((m) => m.label), conditions);
    const identifiers = Array.from(new Set(members.flatMap((m) => m.values.map((v) => v.identifier))));
    const columns = identifiers.map((identifier) => ({ identifier, label: sharedName(members.flatMap((m) => m.values.filter((v) => v.identifier === identifier).map((v) => v.label)), conditions) }));
    const passed = members.filter((m) => m.passed).length;
    return { key, name, source, rows: members, passed, failed: members.length - passed, columns };
  });
}

/** A check's conditions as a report prints them. */
export const conditionText = (row: Pick<CheckRow, 'condition'>): string => row.condition.join(' · ');

/**
 * A step the checks table shows in full: every value it prints is a yes/no
 * the table carries or a number compared beside one, and it has no picture,
 * file or validation of its own — a "verify the current is below the limit"
 * step, one per region. A report that prints the table leaves such a step out
 * of its step listing.
 */
export function shownByChecks(block: StepBlock, rows: CheckRow[]): boolean {
  const mine = rows.filter((r) => r.source === 'flag' && r.step.id === block.id);
  if (mine.length === 0 || block.figures.length || block.files.length || block.validations.length) return false;
  const covered = new Set(mine.flatMap((r) => r.parameterIds));
  return [...block.readings, ...block.unchanged].every((r) => covered.has(r.parameterId));
}

